/**
 * Réservation de la fiche cours v2 (SPEC § 6.2 règle 2) : la logique de l'écran v1
 * `screens/Class/index.tsx` (non modifié, sans hook réutilisable) reproduite À L'IDENTIQUE —
 * mêmes endpoints (book / book-course / book-runclub, DELETE waitlist), mêmes corps de requête
 * (lib/booking.ts, testés), mêmes messages d'erreur, mêmes traces Sentry (tags `category`,
 * `action`, `isWaitlist`, `isMultiCredit`, `mb_error_code`), mêmes invalidations. Chaque bloc cite
 * sa source. Ce qui change est la présentation : erreurs affichées dans le panneau (et plus en toast),
 * choix du forfait dans le panneau (et plus en modale), « Venez à deux » = 2 appels successifs.
 *
 * Et l'attente (retour de Daphné du 07/10 : « en confirmant une réservation, l'affichage de la confirmation était
 * particulièrement long ») : 10,4 s du clic à la confirmation, 20 s à deux, en simulation aux temps mesurés en local.
 * Les requêtes d'un même navigateur s'attendent côté serveur (session PHP ouverte par AuthController, jamais
 * refermée) : la relecture « en parallèle » des crédits, visites, cours et liste d'attente durait 4,5 s, et la v1 la
 * faisait AVANT d'afficher la suite. Quand la suite est la confirmation (`refresh: 'defer'`), la réservation rend la
 * main dès la réponse du serveur ; `refreshAfterBooking` relance d'abord les crédits (premiers servis) et marque le
 * reste à relire, ce que la confirmation fait à son arrivée. Les forfaits relus avant la réservation (v1 `onSubmit`)
 * ne le sont plus quand la liste affichée a moins d'une minute : le serveur relit de toute façon les forfaits et
 * refuse un forfait qui ne s'applique plus (ClassesService::bookClass, `ServicePurchaseRequired`, message traduit).
 */
import * as Sentry from '@sentry/react';
import { sentryTrace } from 'utils/sentryTrace';
import { trackBeginBooking, trackBookClass, trackBookClassFailed } from 'utils/tracking';
import { useQueryClient } from '@tanstack/react-query';
import { useFetchClass, useFetchClassWaitlistEntry, useListClientServices, useListVisits } from 'hooks/useApiQueries';
import { useUserContext } from 'providers/useUserContext';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiClientExceptionErrorCode } from 'types/api';
import type { MindbodyClass, MindbodyWaitlistEntry, PickaspotReservation } from 'types/class';
import type { ClientService } from 'types/client';
import { ApiClientException } from 'utils/apiClientException';
import { HTTP } from 'utils/http';
import { resolveUserError } from 'utils/userErrorMessages';
import { typo } from '../../lib/typo';
import {
  bookEndpoint,
  buildBookBody,
  buildCommunityBody,
  freshCachedServices,
  ROUTINE_SERVICE_IDS,
  type ReservationBody,
  waitlistServiceSelectionTrace,
} from './lib/booking';

export interface BookingError {
  title: string;
  description?: string;
}

export interface UseBookingParams {
  id: string;
  mindbodyClass?: MindbodyClass;
  isEnrollmentInURL: boolean;
  isCourse: boolean;
  isRunClub: boolean;
  isCommunityClass: boolean;
  hasNoRoomLayout: boolean;
  classIsFull: boolean;
  creditsRequired: number;
  waitlistEntry?: MindbodyWaitlistEntry | null;
}

export interface BookArgs {
  spot?: number | null;
  serviceId?: number;
}

/**
 * Relecture après une réservation réussie : `await` = v1 (crédits, visites, cours et liste d'attente relus avant de
 * rendre la main : l'écran reste sur la fiche, liste d'attente) ; `defer` = rien d'attendu, l'écran suivant est la
 * confirmation (l'appelant lance `refreshAfterBooking`).
 */
export type BookingRefresh = 'await' | 'defer';

export interface BookOptions {
  refresh?: BookingRefresh;
  /**
   * Une place est déjà réservée dans ce parcours sans relecture (« Venez à deux », 1re place en `defer`) : en cas
   * d'échec, cours, liste d'attente ET crédits sont relus avant d'afficher l'erreur (la fiche passe en « C'est
   * réservé », avec « Réessayer pour l'invité »), quelle que soit l'erreur.
   */
  afterBookedSeat?: boolean;
}

export function useBooking(p: UseBookingParams) {
  const { id, mindbodyClass, isEnrollmentInURL, isCourse, creditsRequired } = p;
  const isMultiCreditClass = creditsRequired > 1;
  const queryClient = useQueryClient();
  const { user } = useUserContext();
  const { t } = useTranslation();
  const { refetch: refetchVisits } = useListVisits() || {};
  const { refetch: refetchClientServices } = useListClientServices();
  const { refetch: refetchClass } = useFetchClass(id, isEnrollmentInURL);
  const { refetch: refetchWaitlist } = useFetchClassWaitlistEntry(id);

  const [hasAlreadyUsedRoutineService, setHasAlreadyUsedRoutineService] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<BookingError | null>(null);

  const userLabel = `${user?.FirstName} ${user?.LastName}`;

  /**
   * Relectures v1 d'après réservation, dans l'ordre des besoins de l'écran : les crédits d'abord (la session PHP
   * sert les requêtes du navigateur une à une).
   */
  const refreshAll = useCallback(
    () => Promise.all([refetchClientServices(), refetchVisits(), refetchClass(), refetchWaitlist()]),
    [refetchClientServices, refetchVisits, refetchClass, refetchWaitlist],
  );

  /**
   * Après une réservation réussie suivie de la confirmation : relecture des crédits lancée tout de suite (la
   * confirmation s'y raccroche, sans la relancer), visites, cours et liste d'attente marqués à relire (la confirmation
   * relit le cours et les visites à son arrivée ; la liste d'attente l'est au retour sur la fiche). Renvoie l'heure
   * de la réservation (ConfirmationState.bookedAt).
   */
  const refreshAfterBooking = useCallback((): number => {
    const bookedAt = Date.now();
    // refetch() annule une lecture des forfaits partie AVANT la réponse du serveur : seule une lecture partie après
    // peut afficher les crédits restants.
    void refetchClientServices();
    void queryClient.invalidateQueries({ queryKey: ['/client/visits'], refetchType: 'none' });
    void queryClient.invalidateQueries({ queryKey: ['/class', id], refetchType: 'none' });
    return bookedAt;
  }, [refetchClientServices, queryClient, id]);

  /** = Class/index.tsx `bookClass()`. Renvoie true si la réservation a réussi. */
  const postBooking = useCallback(
    async (body: ReservationBody, extra: Record<string, unknown> = {}, options: BookOptions = {}): Promise<boolean> => {
      const { refresh = 'await', afterBookedSeat = false } = options;
      const bookingTrack = { waitlist: !!body.Waitlist, serviceId: body.serviceId, course: !!isCourse };
      trackBeginBooking(mindbodyClass, bookingTrack);
      try {
        await HTTP.post<PickaspotReservation>(bookEndpoint(!!isCourse), body).send();
        trackBookClass(mindbodyClass, bookingTrack);

        // = bookClass() : trace de la réservation réussie.
        sentryTrace('Class booking successful', {
          level: 'info',
          tags: {
            category: 'booking',
            action: 'book_class',
            isWaitlist: body.Waitlist ? 'true' : 'false',
            isEnrollment: body.isEnrollment ? 'true' : 'false',
            isCourse: isCourse ? 'true' : 'false',
            isMultiCredit: isMultiCreditClass ? 'true' : 'false',
          },
          extra: {
            classId: id,
            serviceId: body.serviceId,
            spotNumber: body.SpotNumber,
            isCommunityClass: body.isCommunityClass,
            creditsRequired,
            locationId: mindbodyClass?.Location?.Id,
            locationName: mindbodyClass?.Location?.Name,
            user: userLabel,
            email: user?.Email,
            clientId: user?.Id,
            siteId: mondayMindbody?.siteId,
            ...extra,
          },
        });

        if (refresh === 'await') await refreshAll();
        if (body.serviceId !== undefined && ROUTINE_SERVICE_IDS.includes(body.serviceId)) {
          setHasAlreadyUsedRoutineService(true);
        }
        return true;
      } catch (error) {
        trackBookClassFailed(
          mindbodyClass,
          error instanceof ApiClientException ? error.ErrorCode : undefined,
          bookingTrack,
        );
        // = bookClass() : contexte Sentry de l'échec.
        Sentry.withScope((scope) => {
          scope.setTag('where', 'ClassScreen:bookClass');
          scope.setTag('isMultiCredit', isMultiCreditClass ? 'true' : 'false');
          scope.setExtra('classId', id);
          scope.setExtra('creditsRequired', creditsRequired);
          scope.setExtra('locationId', mindbodyClass?.Location?.Id);
          scope.setExtra('locationName', mindbodyClass?.Location?.Name);
          scope.setExtra('body', body);
          Object.entries(extra).forEach(([key, value]) => scope.setExtra(key, value));
          if (error instanceof ApiClientException) {
            scope.setTag('mb_error_code', String(error.ErrorCode));
            scope.setExtra('mb_message', error.Message);
          }
          Sentry.captureException(error);
        });

        // Message calculé comme en v1 ; affiché après la relecture du cours (pas de clignotement du panneau).
        let message: BookingError;
        if (error instanceof ApiClientException) {
          // Erreur au format WordPress ({ code, message } : garde-fou du staging, refus REST) : ni Message ni
          // ErrorCode. Titre de repli et lecture prudente de Message : sinon TypeError, rien d'affiché (revue r4).
          let title = error.getMessage() || resolveUserError('booking').title;

          // = bookClass() : liste d'attente complète.
          if (
            error.Message?.includes('ClassSignUpsFull') ||
            error.Message?.includes('Registration for the class is full')
          ) {
            title = t("La liste d’attente est complète");
          }

          if (error.ErrorCode === ApiClientExceptionErrorCode.InsufficientCredits) {
            title = error.Message;
          }

          // = bookClass() : erreurs « crédit non applicable » (titre du serveur, description rassurante).
          const creditErrorDescriptions: Record<string, string> = {
            ServiceNotYetActive: t(
              'Votre place n’a pas été réservée et rien n’a été débité. Choisissez une séance après la date d’activation de votre pack, ou utilisez un autre crédit.',
            ),
            ServiceExpiresBeforeClass: t(
              'Votre place n’a pas été réservée et rien n’a été débité. Choisissez une séance avant l’expiration de votre pack, ou renouvelez-le depuis la page Tarifs.',
            ),
            ServiceNotApplicable: t(
              'Votre réservation a été annulée automatiquement et rien n’a été débité. Ce pack semble réservé à un autre studio ou ne pas s’appliquer à cette séance. Essayez un autre crédit, ou contactez-nous si vous pensez qu’il s’agit d’une erreur.',
            ),
          };
          const creditErrorDescription = creditErrorDescriptions[error.ErrorCode as string];

          const apiDescription =
            creditErrorDescription ??
            (error.ErrorCode === ApiClientExceptionErrorCode.InsufficientCredits
              ? undefined
              : resolveUserError('booking', error).description);

          message = { title, description: apiDescription };

          refetchVisits();
          await Promise.all([refetchClass(), refetchWaitlist(), ...(afterBookedSeat ? [refetchClientServices()] : [])]);
        } else {
          message = resolveUserError('booking', error);
          if (afterBookedSeat) {
            refetchVisits();
            await Promise.all([refetchClass(), refetchWaitlist(), refetchClientServices()]);
          }
        }
        setError(message);
        return false;
      }
    },
    [
      isCourse,
      isMultiCreditClass,
      id,
      creditsRequired,
      mindbodyClass,
      userLabel,
      user,
      refreshAll,
      refetchVisits,
      refetchClientServices,
      refetchClass,
      refetchWaitlist,
      t,
    ],
  );

  /** = Class/index.tsx `bookRunClub()`. */
  const bookRunClub = useCallback(async (refresh: BookingRefresh = 'await'): Promise<boolean> => {
    trackBeginBooking(mindbodyClass, { runClub: true });
    try {
      await HTTP.post('/class/book-runclub', { classId: id }).send();
      trackBookClass(mindbodyClass, { runClub: true });

      sentryTrace('Run Club booking successful', {
        level: 'info',
        tags: { category: 'booking', action: 'book_runclub' },
        extra: { classId: id, user: userLabel, email: user?.Email },
      });

      if (refresh === 'await') await refreshAll();
      return true;
    } catch (error) {
      trackBookClassFailed(mindbodyClass, error instanceof ApiClientException ? error.ErrorCode : undefined, {
        runClub: true,
      });
      Sentry.withScope((scope) => {
        scope.setTag('where', 'ClassScreen:bookRunClub');
        scope.setExtra('classId', id);
        if (error instanceof ApiClientException) {
          scope.setTag('mb_error_code', String(error.ErrorCode));
          scope.setExtra('mb_message', error.Message);
        }
        Sentry.captureException(error);
      });

      const bookingFallback = resolveUserError('booking');
      let title = bookingFallback.title;
      let description: string | undefined = bookingFallback.description;

      if (error instanceof ApiClientException) {
        if (error.Message?.includes('ClassCapacityReached')) {
          title = 'Ce Run Club est complet';
          description = 'Toutes les places ont été réservées. Consultez les prochaines séances Run Club.';
        } else if (error.Message?.includes('AlreadyBooked') || error.Message?.includes('already')) {
          title = 'Vous êtes déjà inscrit à ce Run Club';
          description = undefined;
        } else {
          title = error.getMessage() || bookingFallback.title;
          description = resolveUserError('booking', error).description;
        }
      }

      setError({ title, description });
      return false;
    }
  }, [id, mindbodyClass, userLabel, user, refreshAll]);

  /**
   * = Class/index.tsx `onSubmit()` : forfaits relus avant toute réservation, sauf si la liste affichée a moins d'une
   * minute (voir l'en-tête). Une lecture déjà en cours (arrivée sur la fiche) est attendue, pas relancée.
   */
  const freshServices = useCallback(async (): Promise<ClientService[] | null> => {
    const cached = freshCachedServices(queryClient.getQueryState<ClientService[]>(['/client/services', user?.Id]), Date.now());
    if (cached) return cached;
    try {
      const { data } = await refetchClientServices({ cancelRefetch: false });
      if (!data) {
        const err = new Error('Data is undefined');
        Sentry.withScope((scope) => {
          scope.setTag('where', 'ClassScreen:onSubmit');
          scope.setExtra('context', 'Fetching client services');
          scope.setExtra('data', data);
          Sentry.captureException(err);
        });
        throw err;
      }
      return data;
    } catch (e) {
      Sentry.withScope((scope) => {
        scope.setTag('where', 'ClassScreen:onSubmit');
        if (e instanceof ApiClientException) {
          scope.setTag('mb_error_code', String(e.ErrorCode));
          scope.setExtra('mb_message', e.Message);
        }
        Sentry.captureException(e);
      });
      setError(resolveUserError('booking', e));
      return null;
    }
  }, [refetchClientServices, queryClient, user?.Id]);

  /** Forfait choisi s'il est toujours dans les forfaits relus, sinon le premier (= v1 `data[0]`). */
  const pickService = (services: ClientService[], wanted?: number) =>
    services.find((service) => Number(service.Id) === wanted)?.Id ?? services[0]?.Id;

  /**
   * = Class/index.tsx `confirmServiceSelection()` (trace + garde des paramètres manquants) puis
   * `bookClass()`.
   */
  const confirmServiceSelection = useCallback(
    async (
      args: { serviceId?: number; spot?: number | null; isWaitlist?: boolean; seat?: 'member' | 'guest' },
      options: BookOptions = {},
    ) => {
      const { serviceId, spot, isWaitlist } = args;
      sentryTrace('Attempting to confirm service selection', {
        level: 'info',
        tags: { category: 'service-selection' },
        extra: {
          classId: id,
          selectedService: serviceId,
          serviceId,
          isEnrollmentInURL,
          isWaitlist,
          selectedSpot: spot,
          user: userLabel,
          email: user?.Email,
        },
      });

      if (!id || !serviceId || (!isEnrollmentInURL && !p.hasNoRoomLayout && !isCourse && !isWaitlist && !spot)) {
        Sentry.captureMessage('Exiting confirmServiceSelection due to missing parameters', {
          level: 'warning',
          tags: { category: 'service-selection' },
          extra: {
            classId: id,
            selectedService: serviceId,
            serviceId,
            isEnrollmentInURL,
            isWaitlist,
            selectedSpot: spot,
            user: userLabel,
            email: user?.Email,
          },
        });
        setError({
          title: 'Réservation impossible',
          description: !serviceId
            ? 'Aucun crédit n’est disponible sur votre compte pour ce cours. Rien n’a été débité.'
            : 'Choisissez votre spot sur le plan pour confirmer votre réservation.',
        });
        return false;
      }

      const body = buildBookBody({
        classId: id,
        isEnrollmentInURL,
        spot: spot ?? undefined,
        serviceId,
        isWaitlist,
        waitlistEntryId: p.waitlistEntry?.Id,
        hasNoRoomLayout: p.hasNoRoomLayout,
        isCourse: !!isCourse,
      });
      return postBooking(body, args.seat === 'guest' ? { seat: 'guest' } : {}, options);
    },
    [id, isEnrollmentInURL, isCourse, p.hasNoRoomLayout, p.waitlistEntry, userLabel, user, postBooking],
  );

  /** Réserve UNE place (la sienne, ou celle d'un invité quand le client est déjà inscrit). */
  const bookSeat = useCallback(
    async (
      { spot, serviceId }: BookArgs,
      seat: 'member' | 'guest' = 'member',
      options: BookOptions = {},
    ): Promise<{ ok: boolean; serviceId?: number }> => {
      setError(null);
      setIsSubmitting(true);
      try {
        // = onSubmit() : Run Club, flux dédié sans choix de crédits.
        if (p.isRunClub) return { ok: await bookRunClub(options.refresh) };

        const services = await freshServices();
        if (!services) return { ok: false };

        // = onSubmit() : cours communauté (gratuit), spot passé s'il est choisi.
        if (p.isCommunityClass) {
          return { ok: await postBooking(buildCommunityBody(id, isEnrollmentInURL, spot ?? undefined), {}, options) };
        }

        const isWaitlist = p.classIsFull && !p.waitlistEntry ? true : undefined;
        // = onSubmit() : cours complet, inscription en liste d'attente → trace du choix du forfait.
        if (isWaitlist) {
          const trace = waitlistServiceSelectionTrace(services, userLabel, user?.Email);
          sentryTrace(trace.message, trace.context);
        }

        const chosen = pickService(services, serviceId);
        const ok = await confirmServiceSelection(
          {
            serviceId: chosen,
            spot,
            isWaitlist,
            seat,
          },
          options,
        );
        return { ok, serviceId: chosen };
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      p.isRunClub,
      p.isCommunityClass,
      p.classIsFull,
      p.waitlistEntry,
      id,
      isEnrollmentInURL,
      userLabel,
      user,
      bookRunClub,
      freshServices,
      postBooking,
      confirmServiceSelection,
    ],
  );

  /** = onSubmit() : cours complet et client inscrit → retrait de la liste d'attente. */
  const leaveWaitlist = useCallback(async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await HTTP.delete(`/class/${id}/waitlist`).send();
      queryClient.setQueryData(['/class', id, '/waitlist'], null);
      return true;
    } catch (e) {
      Sentry.withScope((scope) => {
        scope.setTag('where', 'ClassScreen:onSubmit');
        scope.setExtra('isEnrollmentInURL', isEnrollmentInURL);
        if (e instanceof ApiClientException) {
          scope.setTag('mb_error_code', String(e.ErrorCode));
          scope.setExtra('mb_message', e.Message);
        }
        Sentry.captureException(e);
      });
      setError(resolveUserError('cancelWaitlist', e));
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [id, isEnrollmentInURL, queryClient]);

  /*
   * Messages de la v1 (locales/fr.json, serveur) affichés dans la typographie de la v2 : apostrophes ’, espaces
   * insécables avant « ? : ; ! » (revue du 05/10). Les textes sources ne changent pas (flag éteint : v1 identique).
   */
  const displayedError = useMemo(
    () => (error ? { title: typo(error.title), description: typo(error.description) } : null),
    [error],
  );

  return {
    bookSeat,
    refreshAfterBooking,
    leaveWaitlist,
    isSubmitting,
    error: displayedError,
    setError,
    hasAlreadyUsedRoutineService,
    setHasAlreadyUsedRoutineService,
  };
}
